/**
 * Last-resort safety net for stale dynamic imports.
 *
 * After a deploy a still-running tab may try to `import()` a chunk hash that
 * no longer exists in dist/. Without this listener the rejection is silent
 * and React shows nothing (the "blue page"). With this listener the page
 * reloads once and picks up the fresh index.html / chunk graph.
 *
 * The sentinel is a timestamp (not a boolean): it gates only the first 10s
 * after a reload, so legitimate chunk failures that happen later in the
 * session can still trigger a recovery without locking the user out.
 *
 * Equivalent in spirit to Vite's built-in `vite:preloadError` handler;
 * Bun.build emits no such event, so we listen to the underlying Promise
 * rejection instead.
 */

import * as Sentry from '@sentry/react';
import { IS_LOCAL } from '../../config/env';

const SENTINEL_KEY = 'prospero_chunk_reload_at';
const SENTINEL_WINDOW_MS = 10_000;

export const DEDUP_WINDOW_MS = 1_000;

export interface ChunkErrorReloaderDeps {
  storage?: Storage;
  reload?: () => void;
  now?: () => number;
  sentry?: SentryReporter;
  dedupState?: DedupState;
}

export interface SentryReporter {
  addBreadcrumb: (b: {
    category?: string;
    level?: string;
    message?: string;
    data?: Record<string, unknown>;
  }) => void;
  captureMessage: (msg: string, level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug') => void;
}

export interface DedupState {
  seenObjects: WeakSet<object>;
  seenStrings: Map<string, number>;
}

/** AttemptChunkReloadDeps extends ChunkErrorReloaderDeps with an optional dev gate. */
export interface AttemptChunkReloadDeps extends ChunkErrorReloaderDeps {
  isLocal?: boolean; // dev gate; defaults to IS_LOCAL
}

/**
 * Module-singleton dedup state shared across all 4 channels that can observe
 * the same chunk rejection: `unhandledrejection`, `error`, `componentDidCatch`,
 * and `errorElement` (RouteErrorScreen). Per-install state is not sufficient
 * because React's render pipeline intercepts lazy-import failures before
 * they surface to the window listeners installed by `installChunkErrorReloader`.
 */
const moduleDedupState: DedupState = {
  seenObjects: new WeakSet(),
  seenStrings: new Map(),
};

interface ResolvedDeps {
  storage: Storage;
  reload: () => void;
  now: () => number;
  sentry: SentryReporter;
  dedupState: DedupState;
}

function getMessage(reason: unknown): string {
  if (typeof reason === 'string') {
    return reason;
  }
  if (reason && typeof reason === 'object') {
    const m = (reason as { message?: unknown }).message;
    return typeof m === 'string' ? m : '';
  }
  return '';
}

function getName(reason: unknown): string {
  if (reason && typeof reason === 'object') {
    const n = (reason as { name?: unknown }).name;
    return typeof n === 'string' ? n : '';
  }
  return '';
}

export function isChunkLoadError(reason: unknown): boolean {
  if (reason && typeof reason === 'object') {
    const candidate = reason as { name?: unknown };
    if (candidate.name === 'ChunkLoadError') {
      return true;
    }
  }
  const message = getMessage(reason);
  if (!message) {
    return false;
  }
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk \S+ failed|Unable to preload CSS|Cannot find module|module specifier .* was not loaded|Failed to load module script/i.test(
    message,
  );
}

function extractChunkUrl(reason: unknown): string | undefined {
  if (reason && typeof reason === 'object') {
    const filename = (reason as { filename?: unknown }).filename;
    if (typeof filename === 'string' && filename.length > 0) {
      return filename;
    }
  }
  const msg = getMessage(reason);
  const match = /(https?:\/\/\S+?\.(?:m?js|css))/i.exec(msg);
  return match?.[1];
}

function markSeen(reason: unknown, state: DedupState, now: number): boolean {
  if (reason !== null && reason !== undefined && typeof reason === 'object') {
    if (state.seenObjects.has(reason as object)) {
      return true;
    }
    state.seenObjects.add(reason as object);
    return false;
  }
  const msg = getMessage(reason);
  const lastSeen = state.seenStrings.get(msg);
  if (lastSeen !== undefined && now - lastSeen < DEDUP_WINDOW_MS) {
    return true;
  }
  if (state.seenStrings.size >= 10) {
    const oldest = state.seenStrings.keys().next().value;
    if (oldest !== undefined) {
      state.seenStrings.delete(oldest);
    }
  }
  state.seenStrings.set(msg, now);
  return false;
}

function runReloadPipeline(
  reason: unknown,
  storage: Storage,
  reload: () => void,
  now: () => number,
  sentry: SentryReporter,
  dedupState: DedupState,
): boolean {
  if (markSeen(reason, dedupState, now())) {
    return false;
  }

  if (!isChunkLoadError(reason)) {
    return false;
  }

  const stored = storage.getItem(SENTINEL_KEY);
  if (stored !== null) {
    const last = Number(stored);
    if (Number.isFinite(last) && now() - last < SENTINEL_WINDOW_MS) {
      const sentinelAgeMs = now() - last;
      const msg = getMessage(reason);
      sentry.addBreadcrumb({
        category: 'chunk-reloader',
        level: 'warning',
        message: 'sentinel blocked reload',
        data: {
          sentinelAgeMs,
          chunkUrl: extractChunkUrl(reason),
          reasonName: getName(reason),
          reasonMessage: msg.slice(0, 200),
          userAgent: navigator.userAgent,
          href: window.location.href,
        },
      });
      sentry.captureMessage('chunk-reloader: blocked by sentinel', 'error');
      return false;
    }
  }

  storage.setItem(SENTINEL_KEY, String(now()));
  reload();
  return true;
}

/**
 * Attempt an automatic page reload to recover from a stale chunk error.
 *
 * Safe to call from any callsite (ErrorBoundary, RouteErrorScreen, listeners).
 * Dedup is enforced via the module-singleton so duplicate calls across
 * channels for the same rejection are no-ops.
 *
 * Returns `true` if a reload was initiated, `false` otherwise.
 */
export function attemptChunkReload(reason: unknown, deps?: AttemptChunkReloadDeps): boolean {
  // Dev gate: never auto-reload in local dev — real bugs should surface as errors.
  if ((deps?.isLocal ?? IS_LOCAL) === true) {
    return false;
  }

  const storage = deps?.storage ?? window.sessionStorage;
  const reload = deps?.reload ?? (() => window.location.reload());
  const now = deps?.now ?? (() => Date.now());
  const sentry = deps?.sentry ?? {
    addBreadcrumb: (b: Parameters<SentryReporter['addBreadcrumb']>[0]) =>
      Sentry.addBreadcrumb(b as Parameters<typeof Sentry.addBreadcrumb>[0]),
    captureMessage: (m: string, l?: Parameters<SentryReporter['captureMessage']>[1]) =>
      Sentry.captureMessage(m, l),
  };
  const dedupState = deps?.dedupState ?? moduleDedupState;

  return runReloadPipeline(reason, storage, reload, now, sentry, dedupState);
}

export function handleChunkRejection(
  reason: unknown,
  deps: Partial<ResolvedDeps> & Pick<ResolvedDeps, 'storage' | 'reload' | 'now'>,
): boolean {
  // Listeners are installed only when !IS_LOCAL — bypass dev gate by design.
  return attemptChunkReload(reason, {
    storage: deps.storage,
    reload: deps.reload,
    now: deps.now,
    sentry: deps.sentry,
    dedupState: deps.dedupState,
    isLocal: false,
  });
}

export function installChunkErrorReloader(deps: ChunkErrorReloaderDeps = {}): () => void {
  const resolved: ResolvedDeps = {
    storage: deps.storage ?? window.sessionStorage,
    reload: deps.reload ?? (() => window.location.reload()),
    now: deps.now ?? (() => Date.now()),
    sentry: deps.sentry ?? {
      addBreadcrumb: (b) => Sentry.addBreadcrumb(b as Parameters<typeof Sentry.addBreadcrumb>[0]),
      captureMessage: (m, l) => Sentry.captureMessage(m, l),
    },
    dedupState: deps.dedupState ?? moduleDedupState,
  };

  const rejListener = (event: PromiseRejectionEvent): void => {
    handleChunkRejection(event.reason, resolved);
  };

  const errorListener = (event: ErrorEvent): void => {
    const reason: unknown = event.error ?? event.message;
    handleChunkRejection(reason, resolved);
  };

  window.addEventListener('unhandledrejection', rejListener);
  window.addEventListener('error', errorListener, { capture: true });

  return () => {
    window.removeEventListener('unhandledrejection', rejListener);
    window.removeEventListener('error', errorListener, { capture: true });
  };
}

export function installChunkErrorDevWarner(deps?: { now?: () => number }): () => void {
  const _now = deps?.now ?? (() => Date.now());
  void _now;

  const rejListener = (event: PromiseRejectionEvent): void => {
    if (!isChunkLoadError(event.reason)) {
      return;
    }
    const url = extractChunkUrl(event.reason);
    console.warn(
      '[Prospero] Chunk load error detected (dev mode — no reload):',
      getMessage(event.reason),
      url ? `chunk: ${url}` : '',
    );
  };

  const errorListener = (event: ErrorEvent): void => {
    const reason: unknown = event.error ?? event.message;
    if (!isChunkLoadError(reason)) {
      return;
    }
    const url = extractChunkUrl(reason);
    console.warn(
      '[Prospero] Chunk load error detected (dev mode — no reload):',
      getMessage(reason),
      url ? `chunk: ${url}` : '',
    );
  };

  window.addEventListener('unhandledrejection', rejListener);
  window.addEventListener('error', errorListener, { capture: true });

  return () => {
    window.removeEventListener('unhandledrejection', rejListener);
    window.removeEventListener('error', errorListener, { capture: true });
  };
}

export const CHUNK_RELOAD_SENTINEL_KEY = SENTINEL_KEY;
export const CHUNK_RELOAD_WINDOW_MS = SENTINEL_WINDOW_MS;
